import { supabase } from '../../../lib/supabase';
import { fetchAll } from '../../../lib/save';
import { recheckJob } from './orderJobs';

/*
  FIXING A LINE OPENHAND DOES NOT KNOW, ordering stage 2, 11 October 2026.

  **What it is for.** A transfer order line whose ClaimSoft number is on no item
  record makes its job Needs you. Here he says which catalog item it is, ONCE:
  the ClaimSoft number is written onto that item's record for good, so the next
  transfer order carrying it is known straight away. That is how the items he
  actually orders get mapped first, which was the plan of 8 October.

  **His answer, never OnHand's.** The search offers candidates, ranked with the
  ClaimSoft vendor's own items first; nothing is linked until he picks one and
  says yes. The link is recorded as matched by hand ("manual"), confirmed by
  him, with the date.

  **His rules from 26 September still hold**: a ClaimSoft number belongs to one
  item only; an item has one main ClaimSoft number; an MCR* number is the extra
  Medicare number, never the main.
*/

const MCR = /^MCR\*/i;
const spellingKey = v => String(v ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const squash = v => String(v ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');

const ITEM_COLS = 'id, oh_number, vendor_part_number, vendor_part_key, our_description, vendor_description, vendor_id, vendor:vendors(name)';

/* The ClaimSoft vendor a line names, as one of OnHand's vendors, if Vendor
   Mapping knows the spelling. */
export async function vendorForSpelling(spelling) {
  const key = spellingKey(spelling);
  if (!key) return null;
  const { data } = await supabase.from('vendor_names').select('vendor_id').eq('spelling_key', key).maybeSingle();
  return data ? data.vendor_id : null;
}

/*
  **Candidates for a line**, searched in the order that is quick and that fits
  how he works, measured 11 October 2026: a search of every item for the number
  anywhere in a part number or description takes 5 to 7 seconds on 354,059
  items, so it is the LAST resort, not the first.

    1. the vendor ClaimSoft names, its items only        quick: the catalog is filed by vendor
    2. the exact number, with and without dashes,        quick: the part number is indexed
       across every vendor                               (0814-0179 and 08140179)
    3. only if both found nothing, every item            slow, and the screen says so

  Within what is found, the ClaimSoft vendor's own items come first, then a part
  number that is the line's number apart from dashes and spaces.
*/
export async function findCandidates({ number, text, vendorId, everywhere = false }) {
  const raw = String(text ?? number ?? '').trim();
  if (raw.length < 2) return { items: [], error: null, searchedEverything: false };
  /* Quoted, so a number with a dot or a star in it (DFCM25.5R, MCR*B-8381480)
     is searched as written rather than read as part of the request. */
  const safe = x => x.replace(/["%,()\\]/g, ' ').trim();
  const q = safe(raw), sq = squash(raw);
  const anyText = v => 'vendor_part_number.ilike."%' + v + '%",our_description.ilike."%' + v + '%",vendor_description.ilike."%' + v + '%"';
  const seen = new Map();
  const add = rows => { for (const it of rows || []) seen.set(it.id, it); };
  try {
    if (vendorId) {
      const { data, error } = await supabase.from('catalog_items').select(ITEM_COLS).eq('vendor_id', vendorId)
        .or(anyText(q) + (sq && sq !== q.toUpperCase() ? ',vendor_part_number.ilike."%' + sq + '%"' : '')).limit(30);
      if (error) return { items: [], error };
      add(data);
    }
    const keys = [...new Set([q.toUpperCase(), sq].filter(Boolean))];
    const { data: exact, error: eErr } = await supabase.from('catalog_items').select(ITEM_COLS).in('vendor_part_key', keys).limit(30);
    if (eErr) return { items: [], error: eErr };
    add(exact);
    if (everywhere || seen.size === 0) {
      const { data, error } = await supabase.from('catalog_items').select(ITEM_COLS).or(anyText(q)).limit(30);
      if (error) return { items: [], error };
      add(data);
    }
  } catch (e) {
    return { items: [], error: { message: e && e.message ? e.message : String(e) } };
  }
  const n = squash(number);
  const score = it => (vendorId && it.vendor_id === vendorId ? 0 : 2) + (n && squash(it.vendor_part_number) === n ? 0 : 1);
  return {
    items: [...seen.values()].sort((a, b) => score(a) - score(b) || a.vendor_part_number.localeCompare(b.vendor_part_number)).slice(0, 25),
    error: null, searchedEverything: everywhere || seen.size === 0,
  };
}

/*
  **Write the ClaimSoft number onto the item, then fill in every open line that
  carries it.** Returns what changed, read back.
*/
export async function teachClaimSoftNumber({ number, catalogItemId, userId }) {
  const key = String(number || '').trim().toUpperCase();
  if (!key || !catalogItemId) return { ok: false, error: { message: 'Pick an item first.' } };

  // Rule 3: one item per ClaimSoft number. Already on an item is an answer, not a write.
  const { data: taken, error: tErr } = await supabase.from('org_item_numbers')
    .select('org_item:org_items(catalog_item_id)').eq('system', 'claimsoft').eq('number_key', key).maybeSingle();
  if (tErr) return { ok: false, error: tErr };
  if (taken && taken.org_item && taken.org_item.catalog_item_id !== catalogItemId) {
    return { ok: false, error: { message: 'ClaimSoft ' + number + ' is already on another item, so nothing was changed.' } };
  }

  // The company's own record of the item, made if it is the first time.
  let { data: org, error: oErr } = await supabase.from('org_items').select('id').eq('catalog_item_id', catalogItemId).maybeSingle();
  if (oErr) return { ok: false, error: oErr };
  if (!org) {
    const ins = await supabase.from('org_items').insert({ catalog_item_id: catalogItemId }).select('id').maybeSingle();
    if (ins.error || !ins.data) return { ok: false, error: ins.error || { message: 'The item record did not save. Nothing was changed.' } };
    org = ins.data;
  }

  if (!taken) {
    const medicare = MCR.test(key);
    if (!medicare) {
      // Rule 1: one main ClaimSoft number per item.
      const { data: main } = await supabase.from('org_item_numbers').select('number')
        .eq('org_item_id', org.id).eq('system', 'claimsoft').eq('is_main', true).maybeSingle();
      if (main) return { ok: false, error: { message: 'That item already has ClaimSoft ' + main.number + ' as its main number. Two ordinary ClaimSoft numbers for one item is not allowed, so nothing was changed.' } };
    }
    const { data: saved, error: nErr } = await supabase.from('org_item_numbers').insert({
      org_item_id: org.id, system: 'claimsoft', number: String(number).trim(),
      is_main: !medicare, reason: medicare ? 'medicare_billing' : null,
      match_method: 'manual', match_status: 'confirmed', confirmed_by: userId || null, confirmed_at: new Date().toISOString(),
    }).select('id').maybeSingle();
    if (nErr || !saved) return { ok: false, error: nErr || { message: 'The ClaimSoft number did not save. Nothing was changed.' } };
  }

  // Fill in every line on an open job that carries this number and is still unknown.
  const { data: item } = await supabase.from('catalog_items').select('vendor_id').eq('id', catalogItemId).maybeSingle();
  const { data: open, error: jErr } = await fetchAll(() => supabase.from('order_jobs')
    .select('id').not('state', 'in', '(done,cancelled)'));
  if (jErr) return { ok: true, error: null, lines: 0, warning: 'The number is saved, but the open jobs could not be read to fill in.' };
  const jobIds = (open || []).map(j => j.id);
  /*
    **Found by comparing the numbers here, not by a pattern search.** A pattern
    search treats `*` as "anything", and every Medicare number has one
    (MCR*B-8381480), so it could have filled in the wrong lines.
  */
  const unknown = [];
  for (let i = 0; i < jobIds.length; i += 150) {
    const { data, error } = await fetchAll(() => supabase.from('order_job_lines')
      .select('id, job_id, item_as_given').in('job_id', jobIds.slice(i, i + 150)).is('catalog_item_id', null));
    if (error) return { ok: true, error: null, lines: 0, warning: 'The number is saved, but the open jobs could not be read to fill in.' };
    unknown.push(...(data || []).filter(l => String(l.item_as_given).trim().toUpperCase() === key));
  }
  let filled = [];
  for (let i = 0; i < unknown.length; i += 150) {
    const { data, error } = await supabase.from('order_job_lines')
      .update({ catalog_item_id: catalogItemId, vendor_id: item ? item.vendor_id : null, updated_at: new Date().toISOString() })
      .in('id', unknown.slice(i, i + 150).map(l => l.id)).is('catalog_item_id', null)
      .select('id, job_id');
    if (error) return { ok: true, error: null, lines: filled.length, warning: 'The number is saved, but some lines could not be filled in: ' + error.message };
    filled = filled.concat(data || []);
  }

  // A job waiting in the queue moves to Ready once nothing on it is unknown and
  // every line has an Account Edge number.
  const nowReady = await recheckJobs([...new Set(filled.map(l => l.job_id))]);
  return { ok: true, error: null, lines: filled.length, nowReady };
}

/* A job's destination, set by him when ClaimSoft named a place OnHand does not
   know. The job's state is worked out again afterwards. */
export async function setDestination(job, destinationId) {
  const { data, error } = await supabase.from('order_jobs').update({ destination_id: destinationId, updated_at: new Date().toISOString() })
    .eq('id', job.id).select('id').maybeSingle();
  if (error || !data) return { ok: false, error: error || { message: 'That did not save.' } };
  await recheckJob(job.id);
  return { ok: true, error: null };
}

/* Re-check several jobs; return the references of those that are now Ready. */
async function recheckJobs(jobIds) {
  const ready = [];
  for (const id of jobIds) {
    const { data: before } = await supabase.from('order_jobs').select('reference, state').eq('id', id).maybeSingle();
    const after = await recheckJob(id);
    if (before && before.state !== 'ready' && after === 'ready') ready.push(before.reference);
  }
  return ready;
}

/* Every vendor, for choosing which one a new item is filed under. */
export async function loadVendorList() {
  const { data, error } = await supabase.from('vendors').select('id, name').order('name');
  return { vendors: data || [], error };
}

/*
  **An item no vendor's file has, added by him**, his word of 11 October 2026:
  *"yes add the create item option."* For lines like the Vive SUP3125 shoes,
  which no file in the catalog carries, so there is nothing to pick.

  **Two things the catalog insists on**, and the screen says so: every item has
  a vendor, and a vendor part number, and no vendor has the same part number
  twice. Answer 19 of 2 October said an item made from a ClaimSoft row should
  leave the vendor part number blank rather than borrow the ClaimSoft one; the
  catalog cannot hold a blank, so the screen STARTS from the ClaimSoft number
  and asks him to check it or type the vendor's own.

  **Flagged so they can be found**: every item added this way is marked for
  review, saying it was added by MedEx by hand and from which ClaimSoft number,
  with the date. **Only an admin can add one**, because the catalog is shared by
  every company; the database refuses anybody else.
*/
export async function createCatalogItem({ vendorId, partNumber, description, fromClaimSoft }) {
  const part = String(partNumber || '').trim();
  const words = String(description || '').trim();
  if (!vendorId) return { ok: false, error: { message: 'Pick the vendor first.' } };
  if (!part) return { ok: false, error: { message: 'The vendor part number cannot be empty.' } };
  if (!words) return { ok: false, error: { message: 'Give it a description.' } };
  const day = new Date().toISOString().slice(0, 10);
  const { data, error } = await supabase.from('catalog_items').insert({
    vendor_id: vendorId, vendor_part_number: part, vendor_description: words, our_description: words,
    status: 'active', needs_review: true,
    review_reason: 'ADDED BY MEDEX BY HAND from ClaimSoft ' + String(fromClaimSoft || '').trim() + ' on ' + day + ', not from a vendor file',
  }).select('id, oh_number, vendor_part_number, our_description, vendor_description, vendor_id, vendor:vendors(name)').maybeSingle();
  if (error) {
    if (error.code === '23505') return { ok: false, error: { message: 'That vendor already has part ' + part + ' in the catalog. Search for it and pick it instead.' } };
    if (error.code === '42501') return { ok: false, error: { message: 'Only an admin can add an item to the catalog.' } };
    return { ok: false, error };
  }
  if (!data) return { ok: false, error: { message: 'The item did not save. Nothing was changed.' } };
  return { ok: true, error: null, item: data };
}

/*
  ===========================================================================
  THE ACCOUNT EDGE NUMBER, FIXED BY HAND. 11 October 2026.
  ===========================================================================

  **His words, on JOB-00003**: *"I have 7 items that do not have an account
  edge number ... We have the account edge item list in a data table correct? I
  should be able to manually match these items that list none."* A line with no
  Account Edge number keeps its job in Needs you, because the Account Edge file
  cannot be built without one.

  **The list searched is the newest Account Edge upload** (11 October), stored
  exactly as Account Edge exported it. He picks the Account Edge item; its Item
  Number (column A) is saved onto the OnHand item as its main Account Edge
  number, matched by hand by him, with the date. **His rules hold**: an Account
  Edge number belongs to one item only, and an item has one main one.
*/
export async function newestLoad(system) {
  const { data } = await supabase.from('reference_loads').select('id, loaded_at').eq('system', system)
    .order('loaded_at', { ascending: false }).limit(1).maybeSingle();
  return data;
}

export async function findAccountEdgeCandidates({ text, prefer = [] }) {
  const raw = String(text ?? '').trim();
  if (raw.length < 2) return { items: [], error: null };
  const load = await newestLoad('account_edge');
  if (!load) return { items: [], error: { message: 'No Account Edge list has been uploaded.' } };
  const q = raw.replace(/["%,()\\]/g, ' ').trim();
  const sq = squash(raw);
  const cols = '"Item Number","Item Name","Primary Vendor","Vendor Item Number","Buy Unit Measure"';
  const like = v => '"Item Number".ilike."%' + v + '%","Vendor Item Number".ilike."%' + v + '%","Item Name".ilike."%' + v + '%"';
  const seen = new Map();
  try {
    for (const v of [...new Set([q, sq].filter(Boolean))]) {
      const { data, error } = await supabase.from('account_edge_items_raw').select(cols).eq('load_id', load.id).or(like(v)).limit(30);
      if (error) return { items: [], error };
      for (const r of data || []) seen.set(String(r['Item Number']).trim().toUpperCase(), r);
    }
  } catch (e) {
    return { items: [], error: { message: e && e.message ? e.message : String(e) } };
  }
  const keys = [...seen.keys()];
  const taken = new Set();
  for (let i = 0; i < keys.length; i += 150) {
    const { data } = await supabase.from('org_item_numbers').select('number_key').eq('system', 'account_edge').in('number_key', keys.slice(i, i + 150));
    for (const r of data || []) taken.add(r.number_key);
  }
  const wanted = prefer.map(squash).filter(Boolean);
  const score = r => {
    const a = squash(r['Item Number']), b = squash(r['Vendor Item Number']);
    return (wanted.includes(a) || wanted.includes(b) ? 0 : 1) + (taken.has(String(r['Item Number']).trim().toUpperCase()) ? 2 : 0);
  };
  const items = [...seen.values()].map(r => ({
    number: String(r['Item Number']).trim(), name: r['Item Name'], vendor: r['Primary Vendor'],
    vendorItem: r['Vendor Item Number'], unit: r['Buy Unit Measure'], taken: taken.has(String(r['Item Number']).trim().toUpperCase()),
  }));
  return { items: items.sort((a, b) => score({ 'Item Number': a.number, 'Vendor Item Number': a.vendorItem }) - score({ 'Item Number': b.number, 'Vendor Item Number': b.vendorItem }) || a.number.localeCompare(b.number)).slice(0, 25), error: null };
}

export async function teachAccountEdgeNumber({ aeNumber, catalogItemId, userId }) {
  const number = String(aeNumber || '').trim();
  const key = number.toUpperCase();
  if (!key || !catalogItemId) return { ok: false, error: { message: 'Pick an Account Edge item first.' } };
  const { data: taken, error: tErr } = await supabase.from('org_item_numbers')
    .select('org_item:org_items(catalog_item_id)').eq('system', 'account_edge').eq('number_key', key).maybeSingle();
  if (tErr) return { ok: false, error: tErr };
  if (taken && taken.org_item && taken.org_item.catalog_item_id !== catalogItemId) {
    return { ok: false, error: { message: 'Account Edge ' + number + ' is already on another item, so nothing was changed.' } };
  }
  let { data: org, error: oErr } = await supabase.from('org_items').select('id').eq('catalog_item_id', catalogItemId).maybeSingle();
  if (oErr) return { ok: false, error: oErr };
  if (!org) {
    const ins = await supabase.from('org_items').insert({ catalog_item_id: catalogItemId }).select('id').maybeSingle();
    if (ins.error || !ins.data) return { ok: false, error: ins.error || { message: 'The item record did not save. Nothing was changed.' } };
    org = ins.data;
  }
  if (!taken) {
    const { data: main } = await supabase.from('org_item_numbers').select('number')
      .eq('org_item_id', org.id).eq('system', 'account_edge').eq('is_main', true).maybeSingle();
    if (main) return { ok: false, error: { message: 'That item already has Account Edge ' + main.number + ' as its main number, so nothing was changed.' } };
    const { data: saved, error: nErr } = await supabase.from('org_item_numbers').insert({
      org_item_id: org.id, system: 'account_edge', number, is_main: true, reason: null,
      match_method: 'manual', match_status: 'confirmed', confirmed_by: userId || null, confirmed_at: new Date().toISOString(),
    }).select('id').maybeSingle();
    if (nErr || !saved) return { ok: false, error: nErr || { message: 'The Account Edge number did not save. Nothing was changed.' } };
  }
  // Every open job carrying this item is checked again: it may be Ready now.
  const { data: lines } = await fetchAll(() => supabase.from('order_job_lines').select('job_id').eq('catalog_item_id', catalogItemId));
  const { data: open } = await fetchAll(() => supabase.from('order_jobs').select('id').not('state', 'in', '(done,cancelled)'));
  const openIds = new Set((open || []).map(j => j.id));
  const nowReady = await recheckJobs([...new Set((lines || []).map(l => l.job_id))].filter(id => openIds.has(id)));
  return { ok: true, error: null, nowReady };
}

/*
  **Every open job with this item, checked again**, for a number removed on the
  item itself (job 37, 12 October 2026). Returns the jobs that were Ready and are
  not any more, so the screen can say so.
*/
export async function recheckJobsWithItem(catalogItemId) {
  const { data: lines } = await fetchAll(() => supabase.from('order_job_lines').select('job_id').eq('catalog_item_id', catalogItemId));
  const ids = [...new Set((lines || []).map(l => l.job_id))];
  if (!ids.length) return [];
  const { data: jobs } = await supabase.from('order_jobs').select('id, reference, state').in('id', ids).not('state', 'in', '(done,cancelled)');
  const back = [];
  for (const j of jobs || []) {
    const after = await recheckJob(j.id);
    if (j.state === 'ready' && after && after !== 'ready') back.push(j.reference);
  }
  return back;
}
